import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { eventHub } from '../../pm-plugins/ai-event-hub/event-hub';
import type { DraftReplyGenerateData } from '../../pm-plugins/ai-event-hub/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';

// config.draftReplyMetadata.category -> `trigger`. The category comes from the payload Jira
// sends with the start prompt (getDraftReplyPillConfigItem copies it into draftReplyMetadata).
// Configs without a mapped category (free generate, other prompts) are ignored.
const TRIGGER: Record<string, DraftReplyGenerateData['trigger'] | undefined> = {
	DRAFT_REPLY: 'SUGGEST_REPLY',
	PILL_RESPONSE: 'PILLS',
	ENHANCE_REPLY: 'ENHANCE_REPLY',
};

// The backend answers 200 with this static text when it has no KB, similar issues or
// transition context. Brittle string match until the response carries a flag.
const NO_SUGGESTION_PREFIX = "We don't have any suggestions right now";

// One in-flight attempt per editor, so a late dismiss cannot abort a finished attempt.
const pendingAttempts = new WeakMap<EditorView, string>();

type PublishDraftReplyGenerateParams = Pick<
	DraftReplyGenerateData,
	'failureReason' | 'statusCode' | 'traceId'
> & {
	configItem: EditorPluginAIConfigItem;
	editorView: EditorView;
	/** Final response text. Only used to classify the outcome, never forwarded. */
	markdown?: string;
	outcome: DraftReplyGenerateData['outcome'];
	/** The backend judged the rewrite too similar to the input. A valid result, not a failure. */
	responseTooSimilar?: boolean;
};

/**
 * Publishes a 'draft reply generate' event so products can measure task success,
 * failure and abort for Draft reply generation.
 */
export const publishDraftReplyGenerate = ({
	editorView,
	configItem,
	outcome,
	markdown,
	responseTooSimilar,
	...details
}: PublishDraftReplyGenerateParams): void => {
	if (!fg('jsm_draft_reply_task_metrics')) {
		return;
	}

	const trigger = TRIGGER[configItem.draftReplyMetadata?.category ?? ''];
	if (!trigger) {
		return;
	}

	let attemptId: string | undefined;
	if (outcome === 'started') {
		attemptId = crypto.randomUUID();
		pendingAttempts.set(editorView, attemptId);
	} else {
		attemptId = pendingAttempts.get(editorView);
		pendingAttempts.delete(editorView);
	}
	if (!attemptId) {
		return;
	}

	let data: DraftReplyGenerateData = { ...details, attemptId, outcome, trigger };
	if (outcome === 'succeeded') {
		const text = markdown?.trim() ?? '';
		if (!text && !responseTooSimilar) {
			data = { ...data, outcome: 'failed', failureReason: 'empty response', statusCode: 200 };
		} else if (trigger === 'SUGGEST_REPLY') {
			data = { ...data, noSuggestion: text.startsWith(NO_SUGGESTION_PREFIX) };
		}
	}

	try {
		eventHub.publishFromEditor({ event: 'draft reply generate', data });
	} catch {
		// Telemetry must never take down the AI session.
	}
};
